import React, { memo } from 'react';
import { Handle, Position, NodeProps } from 'reactflow';
import { useTranslation } from 'react-i18next';
import {
  Clock,
  MessageSquare,
  Timer,
} from 'lucide-react';

interface DelayNodeData {
  label: string;
  type: string;
  icon?: string;
  config: Record<string, any>;
  isConfigured?: boolean;
}

const iconMap: Record<string, React.ReactNode> = {
  Clock: <Clock className="w-5 h-5" />,
  MessageSquare: <MessageSquare className="w-5 h-5" />,
};

const DelayNode: React.FC<NodeProps<DelayNodeData>> = ({ data, selected }) => {
  const { t } = useTranslation();

  const getConfigPreview = () => {
    if (data.type === 'wait' && data.config?.amount && data.config?.unit) {
      const units: Record<string, string> = {
        minutes: 'min',
        hours: 'h',
        days: 'd',
      };
      return `${t('workflow.wait', 'Aguardar')} ${data.config.amount}${units[data.config.unit] || data.config.unit}`;
    }
    if (data.type === 'wait_response' && data.config?.timeoutHours) {
      return `Timeout: ${data.config.timeoutHours}h`;
    }
    return null;
  };

  const configPreview = getConfigPreview();
  const isWaitResponse = data.type === 'wait_response';

  return (
    <div
      className={`relative min-w-[180px] bg-white dark:bg-gray-800 rounded-xl shadow-lg border-2 transition-all ${
        selected
          ? 'border-cyan-500 shadow-cyan-100 dark:shadow-cyan-900/30'
          : 'border-cyan-200 dark:border-cyan-800 hover:border-cyan-400'
      }`}
    >
      {/* Handle - Input */}
      <Handle
        type="target"
        position={Position.Top}
        className="!w-4 !h-4 !bg-cyan-500 !border-2 !border-white dark:!border-gray-800 !rounded-full"
      />

      {/* Header */}
      <div className="bg-gradient-to-r from-cyan-500 to-teal-500 rounded-t-lg px-4 py-2">
        <div className="flex items-center gap-2 text-white">
          <Timer className="w-4 h-4" />
          <span className="text-xs font-medium uppercase tracking-wide">
            {isWaitResponse ? t('workflow.waitResponse', 'Aguardar Resposta') : t('workflow.delayType', 'Atraso')}
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-cyan-100 dark:bg-cyan-900/30 rounded-lg flex items-center justify-center text-cyan-600 dark:text-cyan-400">
            {iconMap[data.icon || 'Clock'] || <Clock className="w-5 h-5" />}
          </div>
          <div className="flex-1">
            <p className="font-medium text-gray-900 dark:text-white text-sm leading-tight">
              {data.label}
            </p>
            {data.isConfigured ? (
              <span className="text-xs text-cyan-600 dark:text-cyan-400">{t('workflow.configured', 'Configurado')}</span>
            ) : (
              <span className="text-xs text-yellow-600 dark:text-yellow-400">{t('workflow.clickToConfigure', 'Clique para configurar')}</span>
            )}
          </div>
        </div>

        {/* Config Preview */}
        {configPreview && (
          <div className="mt-3 p-2 bg-gray-50 dark:bg-gray-900/50 rounded-lg">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {configPreview}
            </p>
          </div>
        )}

        {/* Branch Labels for Wait Response */}
        {isWaitResponse && (
          <div className="mt-3 flex justify-between text-xs">
            <div className="flex items-center gap-1 text-green-600 dark:text-green-400">
              <div className="w-2 h-2 rounded-full bg-green-500" />
              {t('workflow.responded', 'Respondeu')}
            </div>
            <div className="flex items-center gap-1 text-gray-500 dark:text-gray-400">
              Timeout
              <div className="w-2 h-2 rounded-full bg-gray-400" />
            </div>
          </div>
        )}
      </div>

      {/* Handles - Output(s) */}
      {isWaitResponse ? (
        <>
          <Handle
            type="source"
            position={Position.Bottom}
            id="responded"
            className="!w-4 !h-4 !bg-green-500 !border-2 !border-white dark:!border-gray-800 !rounded-full !left-[30%]"
          />
          <Handle
            type="source"
            position={Position.Bottom}
            id="timeout"
            className="!w-4 !h-4 !bg-gray-400 !border-2 !border-white dark:!border-gray-800 !rounded-full !left-[70%]"
          />
        </>
      ) : (
        <Handle
          type="source"
          position={Position.Bottom}
          className="!w-4 !h-4 !bg-cyan-500 !border-2 !border-white dark:!border-gray-800 !rounded-full"
        />
      )}

      {/* Timer Animation */}
      <div className="absolute top-3 right-3">
        <div className="w-6 h-6 rounded-full border-2 border-cyan-300 dark:border-cyan-700 flex items-center justify-center">
          <div className="w-0.5 h-2 bg-cyan-500 rounded-full origin-bottom animate-spin" style={{ animationDuration: '4s' }} />
        </div>
      </div>
    </div>
  );
};

export default memo(DelayNode);
